/**
 * Componente: DashboardStatCard
 * Card de estatística melhorado para a nova dashboard
 */

import React from 'react';
import { LucideIcon } from 'lucide-react';

interface DashboardStatCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  color: 'teal' | 'blue' | 'amber' | 'rose' | 'green' | 'orange';
  /** Texto auxiliar (ex.: quantidade de avaliações) */
  subtitle?: string;
  change?: {
    value: number;
    isPositive: boolean;
  };
  trend?: 'up' | 'down' | 'neutral';
}

const colorConfig = {
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    icon: 'text-teal-600 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-800',
    change: 'text-teal-600 dark:text-teal-400',
  },
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    icon: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    change: 'text-blue-600 dark:text-blue-400',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    icon: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
    change: 'text-amber-600 dark:text-amber-400',
  },
  rose: {
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    icon: 'text-rose-600 dark:text-rose-400',
    border: 'border-rose-200 dark:border-rose-800',
    change: 'text-rose-600 dark:text-rose-400',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    icon: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    change: 'text-green-600 dark:text-green-400',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    icon: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    change: 'text-orange-600 dark:text-orange-400',
  },
};

export function DashboardStatCard({
  label,
  value,
  icon: Icon,
  color,
  subtitle,
  change,
  trend,
}: DashboardStatCardProps) {
  const config = colorConfig[color];

  return (
    <div className={`bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-700 transition-colors group`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {label}
          </p>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
            {value}
          </h3>

          {subtitle && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}

          {change && (
            <div className={`mt-3 flex items-center gap-1 text-sm font-medium`}>
              <span className={change.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {change.isPositive ? '+' : ''}{change.value}%
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                vs last month
              </span>
            </div>
          )}
        </div>

        <div className={`w-12 h-12 rounded-lg ${config.bg} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${config.icon}`} />
        </div>
      </div>
    </div>
  );
}
